import { driver, Driver } from 'driver.js';

import { TourStop } from './tour-stop';

/**
 * Represents a collection of tour stops that form a complete tour sequence.
 *
 * A tour itinerary manages the progression through multiple tour stops and
 * integrates with the driver.js library to present the tour UI. Each itinerary
 * tracks its own progress state and can be activated, deactivated, skipped, or completed.
 *
 * @example
 * ```typescript
 * const stops = [
 *   new TourStopPoi('#step1', 'Step 1', 'Description', { nextButton: {...} }),
 *   new TourStopPoi('#step2', 'Step 2', 'Description', { nextButton: {...} })
 * ];
 *
 * const itinerary = new TourItinerary('Welcome Tour', stops);
 * if (itinerary.canActivate()) {
 *   itinerary.activate();
 * }
 * ```
 */
export class TourItinerary {
  /** The driver.js instance used for presenting the tour UI */
  private driverJs: Driver | null = null;

  /** Whether this itinerary's name should be displayed in the tour progress component */
  showNameInTourProgress: boolean;

  /** Internal progress state of the itinerary */
  private progressSource: 'active' | 'inactive' | 'skipped' = 'inactive';

  /**
   * Tracks if the itinerary has been successfully completed.
   *
   * This is used for checklist-style UIs and is independent of the `progress`
   * state, which reflects whether the tour is currently active.
   */
  isCompleted = false;

  /**
   * Current progress state of the itinerary
   * - 'inactive': Not started yet
   * - 'active': Currently running
   * - 'skipped': Skipped by user
   */
  get progress() {
    return this.progressSource;
  }

  /** Internal index of the current stop within this itinerary */
  private currentStopIndexSource = 0;

  /** Zero-based index of the current stop being displayed */
  get currentStopIndex() {
    return this.currentStopIndexSource;
  }

  /**
   * Creates a new tour itinerary with the specified name and stops.
   *
   * @param name - Display name for the itinerary
   * @param stops - Array of tour stops to include in this itinerary
   * @param associatedUrl - Optional URL path that this itinerary is tied to.
   * @param showNameInTourProgress - Whether to show this itinerary's name in progress UI (default: true)
   * @throws {Error} When stops array is empty
   *
   * @example
   * ```typescript
   * const itinerary = new TourItinerary(
   *   'Setup Tour',
   *   [step1, step2, step3],
   *   true // Show in progress
   * );
   * ```
   */
  constructor(
    public name: string,
    public stops: TourStop[],
    public associatedUrl?: string,
    showNameInTourProgress = true
  ) {
    if (stops.length === 0) {
      throw new Error(
        `Itinerary "${name}": Zero stops. At least one stop is required`
      );
    }

    this.showNameInTourProgress = showNameInTourProgress;
  }

  /**
   * Returns the display name of the itinerary.
   * @returns The itinerary's name as a string.
   */
  getName() {
    return this.name;
  }

  /**
   * Determines if this itinerary can be activated.
   *
   * This method can be overridden by subclasses to implement custom
   * activation logic based on application state or user permissions.
   *
   * @returns True if the itinerary can be activated, false otherwise
   *
   * @example
   * ```typescript
   * if (itinerary.canActivate()) {
   *   itinerary.activate();
   * } else {
   *   console.log('Itinerary cannot be activated');
   * }
   * ```
   */
  canActivate() {
    return true;
  }

  /**
   * Activates this itinerary and begins the tour presentation.
   *
   * This method checks if the itinerary can be activated, sets the progress
   * to 'active', and starts the tour at the current stop index.
   *
   * @returns True if activation was successful, false if it failed
   *
   * @example
   * ```typescript
   * const success = itinerary.activate();
   * if (success) {
   *   console.log('Tour started successfully');
   * }
   * ```
   */
  activate() {
    if (!this.canActivate()) {
      this.endDriverJs();
      return false;
    }

    this.progressSource = 'active';
    this.gotoStop(this.currentStopIndex);
    return true;
  }

  /**
   * Deactivates this itinerary without completing it.
   *
   * This method sets the progress to 'inactive' and stops the driver.js
   * presentation. The itinerary can be reactivated later.
   *
   * @example
   * ```typescript
   * itinerary.deactivate(); // Temporarily stop the tour
   * ```
   */
  deactivate() {
    this.progressSource = 'inactive';
    this.endDriverJs();
  }

  /**
   * Marks this itinerary as skipped by the user.
   *
   * This method sets the progress to 'skipped' and ends the driver.js
   * presentation. Skipped itineraries can be resumed later.
   *
   * @example
   * ```typescript
   * itinerary.skip(); // User chose to skip this itinerary
   * ```
   */
  skip() {
    this.progressSource = 'skipped';
    this.endDriverJs();
  }

  /**
   * Marks this itinerary as completed successfully.
   *
   * This method sets the `isCompleted` flag to true, sets progress to 'inactive',
   * resets the current stop index to 0 for future runs, and ends the driver.js presentation.
   *
   * @example
   * ```typescript
   * itinerary.complete(); // Successfully finished the itinerary
   * ```
   */
  complete() {
    this.isCompleted = true;
    this.progressSource = 'inactive';
    this.currentStopIndexSource = 0;
    this.endDriverJs();
  }

  /**
   * Pauses the tour presentation temporarily.
   *
   * This method stops the driver.js presentation while maintaining
   * the current progress state and stop index.
   *
   * @example
   * ```typescript
   * itinerary.pause(); // Temporarily pause the tour
   * ```
   */
  pause() {
    this.endDriverJs();
  }

  /**
   * Navigates to a specific stop within this itinerary.
   *
   * This method will activate the specified stop if it can be activated,
   * update the current stop index, and restart the driver.js presentation.
   *
   * @param index - Zero-based index of the stop to navigate to
   *
   * @example
   * ```typescript
   * itinerary.gotoStop(2); // Jump to the third stop
   * ```
   */
  gotoStop(index: number) {
    if (this.stops[index].canActivate()) {
      this.currentStopIndexSource = index;

      this.startDriverJs();
      this.driverJs?.moveTo(index);
    }
  }

  /**
   * Advances to the next stop in the itinerary.
   *
   * This method is equivalent to calling gotoStop with the next index.
   *
   * @example
   * ```typescript
   * itinerary.nextStop(); // Move to the next stop
   * ```
   */
  nextStop() {
    this.gotoStop(this.currentStopIndex + 1);
  }

  /**
   * Refreshes the current stop in the driver.js instance.
   *
   * This method is used to refresh the current stop in the driver.js instance
   * when the tour progress component is resized.
   */
  refreshCurrentStop() {
    if (this.driverJs === null) {
      return;
    }

    const stop = this.stops[this.currentStopIndex];
    this.driverJs.highlight(stop.toDriverJsStep());
  }

  /**
   * Initializes and starts the driver.js instance for this itinerary.
   *
   * This private method creates a new driver.js instance with all stops
   * converted to driver.js steps, configures the appearance, and starts
   * the presentation.
   *
   * @private
   */
  private startDriverJs() {
    if (this.driverJs) {
      return;
    }

    this.driverJs = driver({
      steps: this.stops.map((stop) => stop.toDriverJsStep()),
      animate: false,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      allowClose: false,
      overlayOpacity: 1.0,
      stagePadding: 8,
      stageRadius: 4,
      allowKeyboardControl: false,
      popoverClass: 'tour-stop',
    });
    this.driverJs.drive();
  }

  /**
   * Destroys the current driver.js instance.
   *
   * This private method safely destroys the driver.js instance if it exists
   * and sets the reference to null.
   *
   * @private
   */
  private endDriverJs() {
    if (!this.driverJs) {
      return;
    }

    this.driverJs.destroy();
    this.driverJs = null;
  }
}
